import React, {type CSSProperties, type ReactNode} from 'react';
import {controlStyle, fieldLabelStyle, requiredMarkStyle} from '../lib/formStyles.js';
import Badge from '../shared/Badge.js';
import Button from '../shared/Button.js';
import Collapsible from '../shared/Collapsible.js';
import Modal, {ModalFooter} from '../shared/Modal.js';
import {openableProps} from '../shared/openable.js';
import {LockedTeamMemberField} from '../shared/recordPageControls.tsx';

export function modalBackdropStyle(overrides?: CSSProperties): CSSProperties {
  return {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'var(--scrim)',
    zIndex: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    ...overrides,
  };
}

export function modalDialogStyle(overrides?: CSSProperties): CSSProperties {
  return {
    background: 'var(--bg-card)',
    borderRadius: 'var(--r-modal)',
    width: '100%',
    maxWidth: 640,
    boxShadow: 'var(--shadow-modal)',
    ...overrides,
  };
}

// Shared cattle/sheep presentation only. Data, permissions and mutations stay
// with each surface. Q08 fixes palette identity to the canonical group order.
export function RuminantGroupLabel({
  label,
  index,
  archived = false,
}: {
  label: string;
  index: number;
  archived?: boolean;
}) {
  return (
    <span style={{display: 'inline-flex', alignItems: 'center', gap: 7}}>
      <span
        aria-hidden="true"
        style={{
          width: 7,
          height: 7,
          flex: '0 0 7px',
          borderRadius: '50%',
          background: archived || index < 0 ? 'var(--ink-faint)' : `var(--group-${(index % 6) + 1})`,
        }}
      />
      {label}
    </span>
  );
}

type RuminantFlags = {breeding_blacklist?: boolean | null; maternal_issue_flag?: boolean | null};
/**
 * R09: no colored edge stripe. A flagged row is named by RuminantFlagBadge
 * below; a blacklisted row also takes the danger soft tint. Kept as a function
 * so its callers do not change.
 */
export function ruminantFlagStyle(row: RuminantFlags): CSSProperties | undefined {
  if (row.breeding_blacklist) return {background: 'var(--danger-soft)'};
  return undefined;
}
export function RuminantFlagBadge({row}: {row: RuminantFlags}) {
  if (row.breeding_blacklist) return <Badge variant="danger">Breeding Blacklist</Badge>;
  if (row.maternal_issue_flag) return <Badge variant="warn">Maternal Issue</Badge>;
  return null;
}

export function formatRuminantAge(
  birth: string | null | undefined,
  monthSuffix: 'm' | 'mo' = 'm',
  utcNoon = true,
): string | null {
  if (!birth) return monthSuffix === 'mo' ? '' : null;
  if (monthSuffix === 'mo') {
    const b = new Date(birth);
    const now = new Date();
    let y = now.getFullYear() - b.getFullYear();
    let m = now.getMonth() - b.getMonth();
    if (m < 0) {
      y--;
      m += 12;
    }
    if (y > 0) return y + 'y ' + m + 'mo';
    return m + 'mo';
  }
  const suffix = utcNoon ? 'T12:00:00Z' : 'T12:00:00';
  const days = Math.floor((Date.now() - new Date(birth + suffix).getTime()) / 86400000);
  if (days < 0) return null;
  const y = Math.floor(days / 365);
  const m = Math.floor((days % 365) / 30);
  if (y > 0) return y + 'y ' + m + 'm';
  return m + 'm';
}

export function formatRuminantAgeAtDate(
  birth: string | null | undefined,
  endDate: string | null | undefined,
  monthSuffix: 'm' | 'mo' = 'm',
): string | null {
  if (!birth || !endDate) return monthSuffix === 'mo' ? '' : null;
  const birthMs = new Date(String(birth) + 'T12:00:00Z').getTime();
  const endMs = new Date(String(endDate) + 'T12:00:00Z').getTime();
  if (!Number.isFinite(birthMs) || !Number.isFinite(endMs)) return monthSuffix === 'mo' ? '' : null;
  const days = Math.floor((endMs - birthMs) / 86400000);
  if (days < 0) return monthSuffix === 'mo' ? '' : null;
  const y = Math.floor(days / 365);
  const m = Math.floor((days % 365) / 30);
  if (y > 0) return y + 'y ' + m + monthSuffix;
  return m + monthSuffix;
}

export const ruminantFilterChipBaseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontFamily: 'inherit',
  border: '1px solid var(--border-strong)',
  background: 'var(--surface)',
  color: 'var(--ink-muted)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const ruminantFilterChipActiveStyle: CSSProperties = {
  ...ruminantFilterChipBaseStyle,
  border: '1px solid var(--brand)',
  background: 'var(--surface)',
  color: 'var(--brand)',
  fontWeight: 600,
};

// C9 (Table D "Window and year-over-year change"): a window-over-window change
// carries no tone — the arrow and the sign carry the meaning, in gray. Under a
// 2% move it is a flat arrow; a tiny or zero previous value, or a move beyond
// 200%, shows nothing (the copies' rules, unchanged).
export function ruminantTrend(cur: number | null, prev: number | null): ReactNode {
  if (cur == null || prev == null || prev === 0 || Math.abs(prev) < 0.05) return null;
  const pct = ((cur - prev) / prev) * 100;
  if (Math.abs(pct) > 200) return null;
  const arrow = Math.abs(pct) < 2 ? '→' : pct >= 0 ? '↑' : '↓';
  return (
    <span
      className="ruminant-trend"
      data-ruminant-trend={arrow === '→' ? 'flat' : pct >= 0 ? 'up' : 'down'}
      style={{fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginLeft: 4}}
    >
      {arrow} {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

export type RuminantOutcomeRowBase = {
  id: string | number;
  tag?: string | number | null;
  sex?: string | null;
  breed?: string | null;
  death_date?: string | null;
  sale_date?: string | null;
};

export type RuminantProcessingInfo = {
  date: string;
  age?: string | number | null;
};

export type RuminantOutcomeSectionsProps<T extends RuminantOutcomeRowBase> = {
  rows: readonly T[];
  getGroup: (row: T) => string;
  groupLabels: Record<string, string>;
  outcomes: readonly string[];
  stateKeyPrefix: 'cattle' | 'sheep';
  rowIdPrefix: 'cow-' | 'sheep-';
  fmt: (iso: string) => ReactNode;
  setStatusFilter: (outcome: string) => void;
  processingInfo?: (row: T) => RuminantProcessingInfo | null;
  onRowClick?: (row: T) => void;
};

export function RuminantOutcomeSections<T extends RuminantOutcomeRowBase>({
  rows,
  getGroup,
  groupLabels,
  outcomes,
  stateKeyPrefix,
  rowIdPrefix,
  fmt,
  setStatusFilter,
  processingInfo,
  onRowClick,
}: RuminantOutcomeSectionsProps<T>) {
  return (
    <div style={{marginTop: 8}}>
      {outcomes.map((outcome) => {
        const matching = rows.filter((row) => getGroup(row) === outcome);
        if (matching.length === 0) return null;
        return (
          <Collapsible
            key={outcome}
            label={<RuminantGroupLabel label={groupLabels[outcome]} index={-1} archived />}
            count={matching.length}
            defaultOpen={false}
            stateKey={`${stateKeyPrefix}.outcome.${outcome}`}
          >
            <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 8}}>
              <Button variant="secondary" onClick={() => setStatusFilter(outcome)}>
                View All
              </Button>
            </div>

            <div>
              {matching.slice(0, 50).map((row) => {
                const clickable = !!onRowClick;
                const pInfo = processingInfo ? processingInfo(row) : null;
                return (
                  <div key={row.id} id={rowIdPrefix + row.id} style={{borderTop: '1px solid var(--divider)'}}>
                    <div
                      {...(clickable ? openableProps(() => onRowClick(row)) : {})}
                      style={{
                        padding: '8px 16px',
                        fontSize: 13,
                        color: 'var(--ink-muted)',
                        display: 'flex',
                        gap: 10,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        cursor: clickable ? 'pointer' : 'default',
                      }}
                      className={clickable ? 'hoverable-tile wcf-clickable' : ''}
                    >
                      <span style={{fontWeight: 600, color: 'var(--ink)', minWidth: 60}}>
                        {row.tag ? '#' + row.tag : '(no tag)'}
                      </span>
                      <span>{row.sex || '—'}</span>
                      <span>{row.breed || '—'}</span>
                      {row.death_date && <span>{'died ' + fmt(row.death_date)}</span>}
                      {row.sale_date && <span>{'sold ' + fmt(row.sale_date)}</span>}
                      {pInfo && <span style={{color: 'var(--ink-muted)'}}>{'processed ' + fmt(pInfo.date)}</span>}
                      {pInfo && pInfo.age && (
                        <span style={{fontWeight: 600, color: 'var(--ink-muted)'}}>{pInfo.age + ' at processing'}</span>
                      )}
                      {clickable && (
                        <span className="chev" aria-hidden="true" style={{marginLeft: 'auto'}}>
                          ›
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {matching.length > 50 && (
                <div style={{padding: '8px 16px', fontSize: 13, color: 'var(--ink-faint)'}}>
                  {matching.length - 50} more — click &quot;View All&quot; above to filter to this section.
                </div>
              )}
            </div>
          </Collapsible>
        );
      })}
    </div>
  );
}

export type LockedTeamMemberSource = {
  name?: string | null;
  profile?: {name?: string | null; full_name?: string | null} | null;
  user?: {email?: string | null} | null;
};

export type NewRuminantWeighInSession = {
  date: string;
  team_member: string;
  herd: string;
};

export type RuminantGroupOption = {
  v: string;
  l: string;
};

export type RuminantNewWeighInModalProps = {
  title: string;
  groupLabel: string;
  groupPlaceholder: string;
  missingGroupError: string;
  options: readonly RuminantGroupOption[];
  onClose: () => void;
  onCreate: (session: NewRuminantWeighInSession) => Promise<void>;
  authState?: LockedTeamMemberSource | null;
};

export function RuminantNewWeighInModal({
  title,
  groupLabel,
  groupPlaceholder,
  missingGroupError,
  options,
  onClose,
  onCreate,
  authState,
}: RuminantNewWeighInModalProps) {
  const {useState, useEffect} = React;
  const lockedTeamName =
    authState && typeof authState === 'object'
      ? authState.name || authState.profile?.name || authState.profile?.full_name || authState.user?.email || ''
      : '';
  const todayStr = () => {
    const d = new Date();
    return (
      d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    );
  };
  const [team, setTeam] = useState(lockedTeamName);
  const [date, setDate] = useState(todayStr());
  const [herd, setHerd] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const initialDate = React.useRef(date);
  const fieldId = React.useId();
  useEffect(() => {
    setTeam(lockedTeamName);
  }, [lockedTeamName]);
  async function create() {
    if (!team) {
      setErr('Pick a team member.');
      return;
    }
    if (!herd) {
      setErr(missingGroupError);
      return;
    }
    setErr('');
    setBusy(true);
    try {
      await onCreate({date, team_member: team, herd});
    } catch (e) {
      const message = e && typeof e === 'object' && 'message' in e ? (e as {message?: unknown}).message : null;
      setErr('Could not create session: ' + (message ? String(message) : String(e)));
    }
    setBusy(false);
  }
  return (
    <Modal
      title={title}
      size="sm"
      dirty={herd !== '' || date !== initialDate.current}
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <ModalFooter
          primaryLabel="Create Session"
          onPrimary={create}
          primaryDisabled={busy || !team || !herd}
          busy={busy}
          busyLabel="Creating…"
          cancelDisabled={busy}
        />
      }
    >
      <div style={{marginBottom: 14}}>
        {React.createElement(LockedTeamMemberField, {value: team, label: 'Team Member', labelStyle: fieldLabelStyle})}
      </div>
      <div style={{marginBottom: 14}}>
        <label htmlFor={fieldId + '-date'} style={fieldLabelStyle}>
          Date
        </label>
        <input
          id={fieldId + '-date'}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={controlStyle}
        />
      </div>
      <div style={{marginBottom: 14}}>
        <label htmlFor={fieldId + '-group'} style={fieldLabelStyle}>
          {groupLabel}
          <span style={requiredMarkStyle}>*</span>
        </label>
        <select id={fieldId + '-group'} value={herd} onChange={(e) => setHerd(e.target.value)} style={controlStyle}>
          <option value="">{groupPlaceholder}</option>
          {options.map((h) => (
            <option key={h.v} value={h.v}>
              {h.l}
            </option>
          ))}
        </select>
      </div>
      {err && (
        <div role="alert" style={{color: 'var(--danger-ink)', fontSize: 13, marginBottom: 10}}>
          {err}
        </div>
      )}
    </Modal>
  );
}

export type RuminantBatchDetailWeightRow = {
  live_weight?: number | string | null;
  hanging_weight?: number | string | null;
};

export type RuminantBatchTotals = {
  totalLive: number;
  totalHang: number;
  rawYieldPct: number | null;
  yieldPct: number | null;
};

export function computeRuminantBatchTotals(
  rows: readonly RuminantBatchDetailWeightRow[] | null | undefined,
): RuminantBatchTotals {
  const safeRows = Array.isArray(rows) ? rows : [];
  const totalLive = safeRows.reduce((sum, r) => sum + (parseFloat(String(r?.live_weight)) || 0), 0);
  const totalHang = safeRows.reduce((sum, r) => sum + (parseFloat(String(r?.hanging_weight)) || 0), 0);
  const rawYieldPct = totalLive > 0 && totalHang > 0 ? (totalHang / totalLive) * 100 : null;
  const yieldPct = totalLive > 0 && totalHang > 0 ? Math.round((totalHang / totalLive) * 1000) / 10 : null;
  return {totalLive, totalHang, rawYieldPct, yieldPct};
}

export function RuminantBatchStatsGrid({children}: {children: ReactNode}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 8,
        fontSize: 11,
        color: 'var(--ink-muted)',
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

export function RuminantBatchStat({
  label,
  value,
  color = 'var(--ink)',
}: {
  label: ReactNode;
  value: ReactNode;
  color?: string;
}) {
  return (
    <div>
      <div style={{color: 'var(--ink-faint)', fontSize: 11, fontWeight: 500}}>{label}</div>
      <div style={{fontWeight: 600, color}}>{value}</div>
    </div>
  );
}

export const ruminantBatchRowCardStyle: CSSProperties = {
  border: '1px solid var(--divider)',
  borderRadius: 10,
  padding: '8px 10px',
  fontSize: 12,
};

export const ruminantBatchDetachButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--danger-ink)',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  padding: '0 2px',
  fontFamily: 'inherit',
};

export const ruminantBatchWeightLabelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: 'var(--ink-muted)',
  marginBottom: 2,
};

export function RuminantHangYield({value, showTrack = true}: {value: number | null | undefined; showTrack?: boolean}) {
  if (value == null || !Number.isFinite(value)) {
    return showTrack ? (
      <span className="cattle-cell-secondary">—</span>
    ) : (
      <span style={{fontSize: 12, color: 'var(--ink-faint)', fontVariantNumeric: 'tabular-nums'}}>—</span>
    );
  }
  if (!showTrack) {
    return (
      <span style={{fontSize: 12, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums'}}>
        <strong>{value + '%'}</strong>
      </span>
    );
  }
  const width = Math.min(100, Math.max(0, ((value - 45) / 20) * 100));
  return (
    <span className="cattle-batch-yield">
      <span className="cattle-batch-yield-track" aria-hidden="true">
        <span style={{width: width + '%'}} />
      </span>
      <strong>{value}%</strong>
    </span>
  );
}

export const ruminantSavedViewInputStyle: CSSProperties = {
  fontFamily: 'inherit',
  fontSize: 12,
  padding: '6px 10px',
  border: '1px solid var(--border-strong)',
  borderRadius: 10,
  boxSizing: 'border-box',
  background: 'var(--surface)',
  color: 'var(--ink)',
};

export const ruminantSavedViewRadioLabelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  color: 'var(--ink)',
  cursor: 'pointer',
};

export const ruminantWeighInEmptyBoxStyle: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-card)',
  boxShadow: 'var(--shadow-card)',
  padding: '2rem',
  textAlign: 'center',
  color: 'var(--ink-muted)',
  fontSize: 13,
};

export function ruminantWeighInEntryMatchText<T>(
  sessionEntries: readonly T[],
  tagQ: string,
  entryMatchesTag: (entry: T) => boolean,
): string {
  if (tagQ) {
    return sessionEntries.filter(entryMatchesTag).length + ' of ' + sessionEntries.length + ' match';
  }
  return sessionEntries.length + ' ' + (sessionEntries.length === 1 ? 'entry' : 'entries');
}

export function RuminantWeighInsHeaderSummary({
  tagQ,
  tagSearch,
  matchedSessionCount,
  sessionCount,
  totalEntries,
  emphasizeCounts = false,
}: {
  tagQ: string;
  tagSearch: string;
  matchedSessionCount: number | null;
  sessionCount: number;
  totalEntries: number;
  emphasizeCounts?: boolean;
}) {
  if (tagQ) {
    return (
      <span>
        Search <strong>{'#' + tagSearch}</strong>: {matchedSessionCount} session
        {matchedSessionCount === 1 ? '' : 's'}
      </span>
    );
  }
  if (emphasizeCounts) {
    return (
      <span>
        <strong>{sessionCount}</strong> sessions {'·'} <strong>{totalEntries.toLocaleString()}</strong> entries
      </span>
    );
  }
  return (
    <span>
      {sessionCount} sessions {'·'} {totalEntries} total entries
    </span>
  );
}

// ── Shared Animal Detail presentation (CowDetail / SheepDetail) ─────────────
// The cow/sheep record card. These two pages keep their DENSE always-editable
// surface as a ratified exception (record_page_visual_consistency_final_static,
// section 4 "Intentional exceptions - dense animal detail surfaces"), so the
// record-page pattern is NOT applied to them — but the card itself still takes
// the redesign's card radius and shadow (R08) like every other card.
export const ruminantDetailCardStyle: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-card)',
  boxShadow: 'var(--shadow-card)',
  padding: '14px 16px',
  background: 'var(--surface)',
  fontSize: 12,
};

export const ruminantDetailHeaderRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
  paddingBottom: 10,
  marginBottom: 12,
  borderBottom: '1px solid var(--border)',
};

// A well INSIDE that card, not a card: the redesign puts wells at the control
// radius (12px), not the 18px card radius, which would read bulbous nested
// inside one and on a surface this dense.
export const ruminantSectionBoxStyle: CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-control)',
  padding: '10px 12px',
  marginBottom: 10,
};

export const ruminantSectionHeaderStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--ink-muted)',
};

export function RuminantNavigateBackBanner({
  canNavigateBack,
  onNavigateBack,
  backToTag,
  noun,
  linkColor,
}: {
  canNavigateBack?: boolean;
  onNavigateBack?: () => void;
  backToTag?: string | null;
  noun: 'cow' | 'sheep';
  linkColor?: string;
}) {
  if (!canNavigateBack || !onNavigateBack) return null;
  return (
    <div
      style={{
        marginBottom: 10,
        padding: '6px 10px',
        background: 'var(--info-soft)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onNavigateBack();
        }}
        style={{
          color: linkColor || 'var(--brand)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 12,
          padding: 0,
          textDecoration: 'underline',
          fontFamily: 'inherit',
          fontWeight: 600,
        }}
      >
        {'← Back' + (backToTag ? ' to #' + backToTag : '')}
      </button>
      <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>— you navigated here from another {noun}</span>
    </div>
  );
}

export type RuminantPriorTagEntry = {
  tag?: string | null;
  changed_at?: string | null;
  source?: string | null;
};

export function RuminantPriorTagsSection({
  oldTags,
  readOnly,
  editInp,
  onAdd,
  onPatchAt,
  onRemove,
  addColor = 'var(--ink)',
  addBorder = '1px dashed var(--border-strong)',
}: {
  oldTags: readonly RuminantPriorTagEntry[];
  readOnly?: boolean;
  editInp: CSSProperties;
  onAdd: () => void;
  onPatchAt: (idx: number, field: string, value: string) => void;
  onRemove: (idx: number) => void;
  addColor?: string;
  addBorder?: string;
}) {
  return (
    <div style={{marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--divider)'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
        <div style={ruminantSectionHeaderStyle}>Prior Tags</div>
        {!readOnly && (
          <button
            type="button"
            onClick={onAdd}
            style={{
              fontSize: 11,
              color: addColor,
              background: 'none',
              border: addBorder,
              borderRadius: 10,
              padding: '3px 10px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Add Prior Tag
          </button>
        )}
      </div>
      {oldTags.length === 0 && (
        <div style={{fontSize: 11, color: 'var(--ink-faint)', fontStyle: 'italic'}}>No prior tags recorded.</div>
      )}
      {oldTags.map((t, ti) => (
        <div
          key={ti}
          style={{
            display: 'grid',
            gridTemplateColumns: '100px 140px 1fr 30px',
            gap: 8,
            marginBottom: 6,
            alignItems: 'center',
          }}
        >
          <input
            type="text"
            placeholder="Tag #"
            defaultValue={t.tag || ''}
            disabled={readOnly}
            onBlur={(ev) => onPatchAt(ti, 'tag', ev.target.value)}
            style={editInp}
          />
          <input
            type="date"
            defaultValue={(t.changed_at || '').slice(0, 10)}
            disabled={readOnly}
            onBlur={(ev) => onPatchAt(ti, 'changed_at', ev.target.value)}
            style={editInp}
          />
          <select
            defaultValue={t.source || 'manual'}
            disabled={readOnly}
            onChange={(ev) => onPatchAt(ti, 'source', ev.target.value)}
            style={editInp}
          >
            <option value="import">Purchase tag (selling farm)</option>
            <option value="weigh_in">Swapped tag (weigh-in)</option>
            <option value="manual">Other / manual entry</option>
          </select>
          {!readOnly && (
            <button
              type="button"
              title="Remove"
              onClick={() => onRemove(ti)}
              style={{
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 10,
                color: 'var(--danger-ink)',
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
                padding: '4px 6px',
                fontFamily: 'inherit',
              }}
            >
              {'×'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Shared Bulk Import Modal (CattleBulkImport / SheepBulkImport) ────────────
export type RuminantBulkImportValidationRow<TParsed extends Record<string, any> = Record<string, any>> = {
  rowIdx: number;
  parsed: TParsed;
  errors: string[];
  warnings: string[];
};

export type RuminantBulkImportResults = {
  okCount: number;
  failCount: number;
  skipped: number;
  log: Array<{tag?: string | null; ok: boolean; msg?: string}>;
};

export type RuminantBulkImportModalProps<TParsed extends Record<string, any> = Record<string, any>> = {
  title: string;
  startParagraph: string;
  startSteps: readonly string[];
  groupColumnHeader: string;
  groupFieldKey: string;
  stage: string;
  busy: boolean;
  err: string | null;
  rows: readonly RuminantBulkImportValidationRow<TParsed>[];
  progress: {done: number; total: number};
  results: RuminantBulkImportResults | null;
  onClose: () => void;
  onComplete?: () => void;
  onDownloadTemplate: () => void;
  onFile: (file: File | undefined) => void;
  onBack: () => void;
  onCommit: () => void;
  formatExtras: (parsed: TParsed) => string[];
};

export function RuminantBulkImportModal<TParsed extends Record<string, any> = Record<string, any>>({
  title,
  startParagraph,
  startSteps,
  groupColumnHeader,
  groupFieldKey,
  stage,
  busy,
  err,
  rows,
  progress,
  results,
  onClose,
  onComplete,
  onDownloadTemplate,
  onFile,
  onBack,
  onCommit,
  formatExtras,
}: RuminantBulkImportModalProps<TParsed>) {
  // The modal's one primary takes the brand green (section 4 Buttons, C05):
  // program colour is a dot or a tab pill, never a button (R05).
  const btnPrimary: CSSProperties = {
    padding: '9px 18px',
    borderRadius: 'var(--r-pill)',
    border: 'none',
    background: 'var(--brand)',
    color: '#fff',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  const readyCount = rows.filter((r) => r.errors.length === 0).length;
  const errorCount = rows.filter((r) => r.errors.length > 0).length;
  const warnCount = rows.filter((r) => r.warnings.length > 0 && r.errors.length === 0).length;
  const thStyle: CSSProperties = {
    padding: '8px 10px',
    textAlign: 'left',
    borderBottom: '1px solid var(--border)',
    fontWeight: 600,
    color: 'var(--ink-muted)',
  };

  return (
    <div
      style={modalBackdropStyle({
        zIndex: 1000,
        alignItems: 'flex-start',
        padding: '2rem 1rem',
        overflowY: 'auto',
      })}
    >
      <div
        style={modalDialogStyle({
          maxWidth: 1100,
          overflow: 'hidden',
        })}
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{margin: 0, fontSize: 16, color: 'var(--text-primary)', fontWeight: 700}}>{title}</h2>
          <button
            onClick={onClose}
            style={{background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--ink-muted)'}}
            aria-label="Close"
          >
            {'×'}
          </button>
        </div>

        <div style={{padding: '20px'}}>
          {err && (
            <div
              style={{
                background: 'var(--danger-soft)',
                border: '1px solid var(--border)',
                color: 'var(--danger-ink)',
                padding: '10px 14px',
                borderRadius: 10,
                marginBottom: 14,
                fontSize: 13,
              }}
            >
              {err}
            </div>
          )}

          {stage === 'start' && (
            <div>
              <p style={{fontSize: 13, color: 'var(--ink-muted)', marginTop: 0}}>{startParagraph}</p>
              <ol style={{fontSize: 13, color: 'var(--ink-muted)', paddingLeft: 20, lineHeight: 1.7}}>
                {startSteps.map((s, idx) => (
                  <li key={idx}>{s}</li>
                ))}
              </ol>
              <div style={{display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap'}}>
                <Button variant="secondary" onClick={onDownloadTemplate}>
                  {'⬇'} Download Template
                </Button>
                <label style={{...btnPrimary, display: 'inline-block'}}>
                  {'⬆'} Upload Filled Template
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => onFile(e.target.files?.[0])}
                    style={{display: 'none'}}
                  />
                </label>
              </div>
              <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 14, lineHeight: 1.5}}>
                Tip: this importer auto-creates new breeds and origins on commit, so you can type a new selling-farm
                name without setting it up first.
              </div>
            </div>
          )}

          {stage === 'preview' && (
            <div>
              <div style={{display: 'flex', gap: 14, fontSize: 13, marginBottom: 14, flexWrap: 'wrap'}}>
                <Badge variant="ok">
                  {'✓'} {readyCount} ready
                </Badge>
                {warnCount > 0 && (
                  <Badge variant="warn">
                    {'⚠'} {warnCount} with warnings
                  </Badge>
                )}
                {errorCount > 0 && (
                  <Badge variant="danger">
                    {'✗'} {errorCount} with errors (will skip)
                  </Badge>
                )}
              </div>
              <div style={{maxHeight: '55vh', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10}}>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 12}}>
                  <thead style={{position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1}}>
                    <tr>
                      <th style={thStyle}>Row</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Tag</th>
                      <th style={thStyle}>Sex</th>
                      <th style={thStyle}>{groupColumnHeader}</th>
                      <th style={thStyle}>Breed</th>
                      <th style={thStyle}>Origin</th>
                      <th style={thStyle}>Extras</th>
                      <th style={thStyle}>Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const hasErr = r.errors.length > 0;
                      const hasWarn = r.warnings.length > 0;
                      const bg = hasErr ? 'var(--danger-soft)' : hasWarn ? 'var(--warn-soft)' : 'var(--surface)';
                      const extras = formatExtras(r.parsed);
                      return (
                        <tr key={i} style={{background: bg, borderBottom: '1px solid var(--divider)'}}>
                          <td
                            style={{padding: '6px 10px', color: 'var(--ink-faint)', fontVariantNumeric: 'tabular-nums'}}
                          >
                            {r.rowIdx}
                          </td>
                          <td style={{padding: '6px 10px'}}>
                            {hasErr ? (
                              <span style={{color: 'var(--danger-ink)', fontWeight: 600}}>{'✗'} skip</span>
                            ) : hasWarn ? (
                              <span style={{color: 'var(--warn-ink)', fontWeight: 600}}>{'⚠'} ready</span>
                            ) : (
                              <span style={{color: 'var(--ok-ink)', fontWeight: 600}}>{'✓'} ready</span>
                            )}
                          </td>
                          <td style={{padding: '6px 10px', fontWeight: 600}}>{r.parsed.tag || '—'}</td>
                          <td style={{padding: '6px 10px'}}>{r.parsed.sex || '—'}</td>
                          <td style={{padding: '6px 10px'}}>{r.parsed[groupFieldKey] || '—'}</td>
                          <td style={{padding: '6px 10px'}}>{r.parsed.breed || '—'}</td>
                          <td style={{padding: '6px 10px'}}>{r.parsed.origin || '—'}</td>
                          <td style={{padding: '6px 10px', color: 'var(--ink-muted)'}}>
                            {extras.length ? extras.join(' · ') : '—'}
                          </td>
                          <td
                            style={{
                              padding: '6px 10px',
                              color: hasErr ? 'var(--danger-ink)' : 'var(--warn-ink)',
                              fontSize: 11,
                            }}
                          >
                            {[...r.errors, ...r.warnings].join('; ') || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16}}>
                <Button variant="secondary" onClick={onBack} disabled={busy}>
                  Back
                </Button>
                <Button variant="primary" onClick={onCommit} disabled={busy || readyCount === 0}>
                  Commit {readyCount} row{readyCount === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          )}

          {stage === 'committing' && (
            <div style={{textAlign: 'center', padding: '2rem 0'}}>
              <div style={{fontSize: 14, color: 'var(--ink-muted)', marginBottom: 14}}>
                Importing {progress.done} of {progress.total}
                {'…'}
              </div>
              <div
                style={{
                  height: 8,
                  background: 'var(--divider)',
                  borderRadius: 4 /* radius-allow: 8px-tall progress bar track */,
                  overflow: 'hidden',
                  maxWidth: 400,
                  margin: '0 auto',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    // D08: a gray track with a green fill.
                    background: 'var(--brand)',
                    width: (progress.total ? (progress.done / progress.total) * 100 : 0) + '%',
                    transition: 'width 0.2s',
                  }}
                />
              </div>
            </div>
          )}

          {stage === 'done' && results && (
            <div>
              <div style={{display: 'flex', gap: 14, fontSize: 13, marginBottom: 16, flexWrap: 'wrap'}}>
                <Badge variant="ok">
                  {'✓'} {results.okCount} imported
                </Badge>
                {results.failCount > 0 && (
                  <Badge variant="danger">
                    {'✗'} {results.failCount} failed
                  </Badge>
                )}
                {results.skipped > 0 && <Badge variant="neutral">skipped {results.skipped} with errors</Badge>}
              </div>
              <div
                style={{
                  maxHeight: '40vh',
                  overflowY: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 14px',
                }}
              >
                {results.log.map((l, i) => (
                  <div
                    key={i}
                    style={{fontSize: 12, padding: '3px 0', color: l.ok ? 'var(--ink)' : 'var(--danger-ink)'}}
                  >
                    {l.ok ? '✓' : '✗'} #{l.tag}
                    {l.msg ? ' — ' + l.msg : ''}
                  </div>
                ))}
              </div>
              <div style={{display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16}}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    onComplete && onComplete();
                    onClose();
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          )}

          {busy && stage !== 'committing' && (
            <div style={{textAlign: 'center', padding: 14, color: 'var(--ink-muted)', fontSize: 13}}>Working{'…'}</div>
          )}
        </div>
      </div>
    </div>
  );
}
